import { useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { normalizarNombre } from '@eventos-cien/shared';
import type { Invitacion } from '@eventos-cien/shared';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { EstadoInvitacionBadge } from '../components/EstadoInvitacionBadge';
import { TextField } from '../components/TextField';
import { db } from '../lib/firebase';
import { usePanelData } from './PanelDataProvider';

export function InvitadosDeAsesorScreen() {
  const { user } = useAuth();
  const { eventoId, invitaciones: todas, mesas } = usePanelData();
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'registrada' | 'entregada' | 'por_enviar'>('todos');
  const [abierta, setAbierta] = useState<string | null>(null);

  const mias = useMemo(() => todas.filter((i) => i.asesorUid === user?.uid), [todas, user]);

  const filtradas = useMemo(() => {
    return mias.filter((i) => {
      const norm = normalizarNombre(q);
      const coincideNombre = !norm || i.nombreNormalizado?.includes(norm) || i.invitado.nombre?.toLowerCase().includes(q.toLowerCase());

      let coincideEstado = true;
      if (filtroEstado === 'registrada') coincideEstado = i.estado === 'registrada';
      if (filtroEstado === 'entregada') coincideEstado = i.estado === 'entregada';
      if (filtroEstado === 'por_enviar') coincideEstado = i.estado === 'disponible' || i.estado === 'en_mano';

      return coincideNombre && coincideEstado;
    });
  }, [mias, q, filtroEstado]);

  const conteos = useMemo(() => ({
    todos: mias.length,
    registrada: mias.filter((i) => i.estado === 'registrada').length,
    entregada: mias.filter((i) => i.estado === 'entregada').length,
    por_enviar: mias.filter((i) => i.estado === 'disponible' || i.estado === 'en_mano').length,
  }), [mias]);

  return (
    <div className="w-full max-w-full space-y-4">
      {/* Buscador de ancho completo */}
      <div className="w-full">
        <TextField
          label="Buscar invitado"
          type="search"
          placeholder="Escribe un nombre..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {/* Filtros de Estado: flex-wrap para 0 scroll horizontal en celulares */}
      <div className="flex w-full flex-wrap gap-1.5 sm:gap-2">
        <Chip activo={filtroEstado === 'todos'} onClick={() => setFiltroEstado('todos')}>
          Todos ({conteos.todos})
        </Chip>
        <Chip activo={filtroEstado === 'registrada'} onClick={() => setFiltroEstado('registrada')}>
          Registrados ({conteos.registrada})
        </Chip>
        <Chip activo={filtroEstado === 'entregada'} onClick={() => setFiltroEstado('entregada')}>
          Enviadas ({conteos.entregada})
        </Chip>
        <Chip activo={filtroEstado === 'por_enviar'} onClick={() => setFiltroEstado('por_enviar')}>
          Por enviar ({conteos.por_enviar})
        </Chip>
      </div>

      {/* Tarjeta y Lista adaptables */}
      <Card className="w-full overflow-hidden bg-white shadow-sm">
        <div className="divide-y divide-borde/50">
          {filtradas.length === 0 ? (
            <div className="p-6 text-center text-sm text-texto/50 sm:p-8">
              No se encontraron invitados.
            </div>
          ) : (
            filtradas.map((i) => (
              <div key={i.token} className="w-full">
                <button
                  type="button"
                  className="flex w-full flex-col gap-2 p-3.5 text-left transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:p-4"
                  onClick={() => setAbierta(abierta === i.token ? null : i.token)}
                >
                  {/* Información del invitado con truncado estricto */}
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <strong className="truncate text-sm text-texto">
                        {i.invitado.nombre || <em className="font-normal text-texto/40">Sin registrar</em>}
                      </strong>
                      <span className="shrink-0 font-mono text-xs text-texto/40">
                        ({i.cupo === 2 ? '👥 Pareja' : '👤 Ind.'})
                      </span>
                    </span>

                    {i.invitado.acompanante ? (
                      <span className="truncate text-xs text-texto/50">+ {i.invitado.acompanante}</span>
                    ) : (
                      i.cupo === 2 &&
                      i.estado === 'registrada' && (
                        <span className="truncate text-xs italic text-texto/40">Sin acompañante</span>
                      )
                    )}

                    {i.invitado.telefono && (
                      <span className="truncate text-xs text-texto/50">📞 {i.invitado.telefono}</span>
                    )}
                  </span>

                  {/* Estado y Mesa asignada */}
                  <span className="flex shrink-0 items-center justify-between gap-2 pt-1 sm:justify-end sm:pt-0">
                    {i.mesaId && (
                      <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-xs font-medium text-navy">
                        Mesa {mesas.find((m) => m.id === i.mesaId)?.numero ?? '?'}
                      </span>
                    )}
                    <EstadoInvitacionBadge estado={i.estado} />
                  </span>
                </button>

                {abierta === i.token && <FichaSoloContacto eventoId={eventoId} invitacion={i} />}
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function FichaSoloContacto({ eventoId, invitacion }: { eventoId: string; invitacion: Invitacion }) {
  const [nombre, setNombre] = useState(invitacion.invitado.nombre);
  const [llevaAcompanante, setLlevaAcompanante] = useState(Boolean(invitacion.invitado.acompanante));
  const [acompanante, setAcompanante] = useState(invitacion.invitado.acompanante ?? '');
  const [telefono, setTelefono] = useState(invitacion.invitado.telefono);
  const [notas, setNotas] = useState(invitacion.invitado.notas);
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);

  async function guardar() {
    setGuardando(true);
    setOk(false);
    try {
      const conAcompanante = invitacion.cupo === 2 && llevaAcompanante;
      await updateDoc(doc(db, 'eventos', eventoId, 'invitaciones', invitacion.token), {
        'invitado.nombre': nombre,
        'invitado.acompanante': conAcompanante ? acompanante : null,
        'invitado.telefono': telefono,
        'invitado.notas': notas,
        nombreNormalizado: normalizarNombre(nombre),
        telefonoUlt4: telefono.replace(/\D/g, '').slice(-4),
      });
      setOk(true);
      setTimeout(() => setOk(false), 1600);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="w-full space-y-3 border-t border-borde bg-slate-50/50 p-3.5 sm:p-4">
      <TextField label="Nombre del invitado" value={nombre} onChange={(e) => setNombre(e.target.value)} />

      {invitacion.cupo === 2 && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-texto">¿Lleva acompañante?</span>
          <div className="flex gap-2">
            <Chip activo={llevaAcompanante} onClick={() => setLlevaAcompanante(true)}>
              Sí lleva
            </Chip>
            <Chip
              activo={!llevaAcompanante}
              onClick={() => {
                setLlevaAcompanante(false);
                setAcompanante('');
              }}
            >
              No lleva
            </Chip>
          </div>
          {llevaAcompanante && (
            <TextField
              label="Nombre del acompañante"
              value={acompanante}
              onChange={(e) => setAcompanante(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="grid w-full gap-3 sm:grid-cols-2">
        <TextField label="Teléfono de contacto" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
        <TextField label="Notas internas" value={notas} onChange={(e) => setNotas(e.target.value)} />
      </div>
      <div className="flex w-full justify-end pt-1">
        <Button variante="secundario" cargando={guardando} onClick={guardar} className="w-full sm:w-auto sm:px-6">
          {ok ? 'Guardado ✓' : 'Guardar datos'}
        </Button>
      </div>
    </div>
  );
}
